import { isRouteErrorResponse } from "react-router-dom";

export class CustomError extends Error {
  public title: string;

  constructor(title: string, message: string) {
    super(message);
    this.name = "CustomError";
    this.title = title;
  }
}

export class ProblemDetailError extends Error {
  public problem: ProblemDetail;

  constructor(problem: ProblemDetail) {
    super(problem.detail || problem.title);
    this.name = "ProblemDetailError";
    this.problem = problem;
  }
}

export class NotFoundError extends CustomError {
  constructor() {
    super(
      "Not Found",
      "We're sorry, we couldn't find the page you were looking for.",
    );
  }
}

export class InternalServerError extends CustomError {
  constructor() {
    super(
      "Internal Server Error",
      "We're sorry, it looks like our servers are having some trouble processing your request.",
    );
  }
}

export interface ProblemDetail {
  title: string;
  status: number;
  detail: string;
  instance?: string;
  type?: string;
  id?: string;
  code?: string;
  errors?: FieldError[];
}

export interface FieldError {
  [field: string]: string | string[];
}

export const isProblemDetail = (response: any): response is ProblemDetail => {
  return (
    typeof response === "object" &&
    response !== null &&
    (response as ProblemDetail).title !== undefined &&
    (response as ProblemDetail).status !== undefined
  );
};

export const is404 = (error: any): boolean => {
  return errorStatus(error) === 404;
};

export const errorStatus = (error: any): number | null => {
  return isRouteErrorResponse(error)
    ? error.status
    : error instanceof ProblemDetailError
      ? error.problem.status
      : (error?.response?.status ?? null);
};

export const errorTitleFallback = "Application Error";

export const errorTitle = (error: unknown): string => {
  if (error && isRouteErrorResponse(error)) return error.data;
  if (error && error instanceof ProblemDetailError) return error.problem.title;
  if (error && error instanceof CustomError) return error.title;
  return errorTitleFallback;
};

export const errorMessageFallback = "An unexpected error occurred";

export const errorMessage = (error: unknown): string => {
  if (error && isRouteErrorResponse(error)) return error.statusText;
  if (error && error instanceof ProblemDetailError) return error.problem.detail;
  if (error && (error instanceof CustomError || error instanceof Error))
    return error.message;
  return errorMessageFallback;
};

export const getFieldErrors = (
  field: string,
  errors: FieldError[],
): string[] => {
  return errors
    ? errors.flatMap((error) => {
        const fieldError = error[field];
        return Array.isArray(fieldError) ? fieldError : [fieldError];
      })
    : [];
};

export const formError = (error: ProblemDetailError | CustomError | Error) => {
  if (isProblemDetail(error)) {
    return {
      genericErrors: [error.title],
      fieldErrors: error.errors ?? [],
    };
  } else {
    return {
      genericErrors: ["Request failed"],
      fieldErrors: [],
    };
  }
};
